import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { TemplatesList } from './TemplatesList';
import { TemplateDetails } from './TemplateDetails';
import { SiteHeader } from '@/components/site-header';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';

interface TemplatesDashboardProps {
    initialTemplateId?: string | null;
}

export function TemplatesDashboard({ initialTemplateId }: TemplatesDashboardProps) {
    const navigate = useNavigate();
    const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(initialTemplateId || null);

    // Update selected template when initialTemplateId changes
    useEffect(() => {
        if (initialTemplateId) {
            setSelectedTemplateId(initialTemplateId);
        }
    }, [initialTemplateId]);

    const handleTemplateSelect = (templateId: string) => {
        setSelectedTemplateId(templateId);
        // Update URL to include template ID
        navigate(`/dashboard/workflows/templates/${templateId}`, { replace: true });
    };

    const handleBackToList = () => {
        setSelectedTemplateId(null);
        // Update URL back to templates list
        navigate('/dashboard/workflows/templates', { replace: true });
    };

    const handleBackToWorkflows = () => {
        navigate('/dashboard/workflows');
    };

    if (selectedTemplateId) {
        return <TemplateDetails templateId={selectedTemplateId} onBack={handleBackToList} />;
    }

    return (
        <>
            <TemplatesList 
                onTemplateSelect={handleTemplateSelect}
                renderHeader={(hasTemplates, searchBar) => (
                    <SiteHeader 
                        title="Templates"
                        subtitle="Browse and use workflow templates to quickly create new flows"
                        backButton={
                            <Button
                                variant="ghost"
                                size="sm"
                                onClick={handleBackToWorkflows}
                                className="gap-2"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back
                            </Button>
                        }
                    >
                        {searchBar}
                    </SiteHeader>
                )}
            />
        </>
    );
}
